// Other Imports Removed for Brevity

import { IconCaretDown, IconCheck, IconLoader2 } from '@tabler/icons-react'
import { useTranslation } from 'i11n/client'
import { forwardRef, useCallback, useMemo, useState } from 'react'
import { cn } from '../utils/cn'
import { Button, ButtonProps } from './Button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './Command'
import { Popover, PopoverContent, PopoverTrigger } from './Popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip'

export interface ComboboxOption {
  searchText?: string
  label: React.ReactNode | string
  description?: string
  groupLabel?: string
  value: string
  wildcardHidden?: boolean
}

type ComboboxPropsSingle = {
  withTooltipValue?: boolean
  readonly?: boolean
  loading?: boolean
  options: ComboboxOption[]
  emptyText?: string
  clearable?: boolean
  searchValue?: string
  onSearchValueChange?: (value: string) => void
  selectPlaceholder?: string
  searchPlaceholder?: string
  multiple?: false
  value?: string
  onValueChange?: (value: string) => void
  classNames?: {
    button?: string
    floatContainer?: string
  }
  properties?: {
    button?: ButtonProps
  }
}

type ComboboxPropsMultiple = {
  withTooltipValue?: boolean
  readonly?: boolean
  withSelectAll?: boolean
  loading?: boolean
  options: ComboboxOption[]
  emptyText?: string
  clearable?: boolean
  searchValue?: string
  onSearchValueChange?: (value: string) => void
  selectPlaceholder?: string
  searchPlaceholder?: string
  multiple: true
  value?: string[]
  onValueChange?: (value: string[]) => void
  classNames?: {
    button?: string
    container?: string
    floatContainer?: string
  }
  properties?: {
    button?: ButtonProps
  }
}

const normalizeString = (value: string = '') =>
  value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

export type ComboboxProps = ComboboxPropsSingle | ComboboxPropsMultiple

export const handleSingleSelect = (
  props: ComboboxPropsSingle,
  option: ComboboxOption,
) => {
  if (props.clearable) {
    props.onValueChange?.(option.value === props.value ? '' : option.value)
  } else {
    props.onValueChange?.(option.value)
  }
}

export const handleMultipleSelect = (
  props: ComboboxPropsMultiple,
  option: ComboboxOption,
) => {
  if (props.value?.includes(option.value)) {
    if (!props.clearable && props.value.length === 1) return false
    props.onValueChange?.(props.value.filter((value) => value !== option.value))
  } else {
    props.onValueChange?.([...(props.value ?? []), option.value])
  }
}

const isOptionSelected = (
  props: Pick<ComboboxProps, 'value' | 'multiple'>,
  option: ComboboxOption,
) => {
  if (props.multiple) return props.value?.includes(option.value)

  return props.value === option.value
}

export const ComboboxSelect = forwardRef(
  (
    {
      properties,
      classNames,
      readonly,
      loading = false,
      withTooltipValue = false,
      options: unsortedOptions,
      ...props
    }: ComboboxProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [open, setOpen] = useState(false)
    const translate = useTranslation()

    const options = useMemo(
      () =>
        [...unsortedOptions].sort((a, b) => {
          if (a.value === 'all' && b.value !== 'all') return -1

          if (a.value !== 'all' && b.value === 'all') return 1

          if (isOptionSelected(props, a) && !isOptionSelected(props, b))
            return -1

          if (!isOptionSelected(props, a) && isOptionSelected(props, b))
            return 1

          return 0
        }),
      [unsortedOptions, JSON.stringify(props.value || {})],
    )

    const areAllOptionsSelected = useMemo(() => {
      const flatOptionsCount = options.filter(
        (option) => !option.wildcardHidden,
      )

      return props.multiple && props.value?.length === flatOptionsCount.length
    }, [props.multiple, props.value, options])
    const searchPlaceholder = useMemo(
      () => props.searchPlaceholder ?? translate('Busca una opción'),
      [props.searchPlaceholder, translate],
    )
    const emptyText = useMemo(
      () => props.emptyText ?? translate('No se encontraron resultados'),
      [props.emptyText, translate],
    )
    const selectedManyText = useMemo(
      () => translate('opciones seleccionadas'),
      [translate],
    )
    const selectAllLabel = useMemo(
      () => translate('Seleccionar todos'),
      [translate],
    )
    const deselectAllLabel = useMemo(
      () => translate('Deseleccionar todos'),
      [translate],
    )
    const handleSelectAll = useCallback(() => {
      if (props.multiple && props.onValueChange) {
        if (areAllOptionsSelected) {
          props.onValueChange([])
        }

        if (!areAllOptionsSelected) {
          props.onValueChange(
            options
              .filter((opt) => !opt.wildcardHidden)
              .map((opt) => opt.value),
          )
        }
      }
    }, [areAllOptionsSelected, options, props])

    const groupedOptions = useMemo(() => {
      const groupedOptionsByLabel = options.reduce<
        Record<string, ComboboxOption[]>
      >((currentGroupedOptionsByLabel, option) => {
        const groupLabel = option.groupLabel || ''
        const currentOptions = currentGroupedOptionsByLabel[groupLabel] || []

        return {
          ...currentGroupedOptionsByLabel,
          [groupLabel]: currentOptions.concat(option),
        }
      }, {})

      return Object.entries(groupedOptionsByLabel)
    }, [options])

    return (
      <Popover
        open={open}
        onOpenChange={(open) => {
          if (readonly) return

          setOpen(open)
        }}
      >
        <PopoverTrigger asChild>
          <Button
            role='combobox'
            variant='outline'
            aria-expanded={open}
            className={cn(
              'w-full justify-between hover:bg-secondary/20 active:scale-100',
              classNames?.button,
            )}
            {...properties?.button}
          >
            <span className='line-clamp-1 text-left font-normal'>
              {props.multiple && props.value && props.value.length === 1 && (
                <span role='combobox-value' className='mr-2'>
                  {props.value
                    .map(
                      (value) =>
                        options.find((opt) => opt.value === value)?.label ||
                        translate('Opción no encontrada'),
                    )
                    .join(', ')}
                </span>
              )}

              {props.multiple && props.value && props.value.length > 1 && (
                <>
                  {withTooltipValue ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger>
                          <span role='combobox-value' className='mr-2'>
                            {props.value.length} {selectedManyText}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <ul className='flex flex-col space-y-1'>
                            {props.value.map((value, index) => {
                              const option = options.find(
                                (opt) => opt.value === value,
                              )!

                              return (
                                <li className='text-xs font-normal' key={index}>
                                  {option.label}
                                </li>
                              )
                            })}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span role='combobox-value' className='mr-2'>
                      {props.value.length} {selectedManyText}
                    </span>
                  )}
                </>
              )}

              {!props.multiple &&
                props.value &&
                props.value !== '' &&
                options.find((option) => option.value === props.value)?.label}

              {(!props.value || props.value.length === 0) &&
                (props.selectPlaceholder ?? 'Select an option')}
            </span>
            {loading ? (
              <IconLoader2 className='ml-2 h-4 w-4 shrink-0 animate-spin opacity-50' />
            ) : (
              <IconCaretDown
                className={cn(
                  'ml-2 h-4 w-4 shrink-0 rotate-0 opacity-50 transition-transform',
                  open && 'rotate-180',
                )}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className={cn(
            'w-[var(--radix-popover-trigger-width)] p-0',
            classNames?.floatContainer,
          )}
        >
          <Command
            filter={(value, search) => {
              const option = options.find(
                (opt) => opt.value.toLowerCase().trim() === value,
              )
              const searchableText = [
                option?.searchText,
                option?.groupLabel,
                option?.label?.toString(),
                option?.description,
              ]
                .filter(Boolean)
                .join(' ')

              if (
                normalizeString(searchableText).includes(
                  normalizeString(search),
                )
              )
                return 1

              return 0
            }}
          >
            <CommandInput
              ref={ref}
              onValueChange={props.onSearchValueChange}
              placeholder={props.searchPlaceholder ?? 'Busca una opción'}
              value={props.searchValue}
            />
            <CommandEmpty>
              {props.emptyText ?? 'No se encontraron resultados'}
            </CommandEmpty>
            <CommandList>
              <div className={cn('max-h-60 overflow-x-hidden overflow-y-auto')}>
                {props.multiple && props.withSelectAll && (
                  <CommandGroup>
                    <CommandItem
                      value='_all'
                      onSelect={() => {
                        if (props.multiple && props.onValueChange) {
                          if (areAllOptionsSelected)
                            return props.onValueChange([])
                          props.onValueChange(
                            options
                              .filter((opt) => !opt.wildcardHidden)
                              .map((opt) => opt.value),
                          )
                        }
                      }}
                    >
                      <IconCheck
                        className={cn('mr-2 h-4 w-4 opacity-0 flex-shrink-0', {
                          'opacity-100': areAllOptionsSelected,
                        })}
                      />
                      {areAllOptionsSelected
                        ? deselectAllLabel
                        : selectAllLabel}
                    </CommandItem>
                  </CommandGroup>
                )}

                {groupedOptions.map(([groupLabel, groupedOptions], index) => (
                  <div key={groupLabel || `_default-${index}`}>
                    {index > 0 ? <CommandSeparator /> : null}
                    <CommandGroup heading={groupLabel || undefined}>
                      {groupedOptions.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value.toLowerCase().trim()}
                          onSelect={(selectedValue) => {
                            const option = options.find(
                              (option) =>
                                option.value.toLowerCase().trim() ===
                                selectedValue,
                            )

                            if (!option) return null

                            if (props.multiple) {
                              handleMultipleSelect(
                                {
                                  ...props,
                                  options,
                                },
                                option,
                              )
                            } else {
                              handleSingleSelect(
                                {
                                  ...props,
                                  options,
                                },
                                option,
                              )

                              setOpen(false)
                            }
                          }}
                        >
                          <IconCheck
                            className={cn(
                              'mr-2 h-4 w-4 opacity-0 flex-shrink-0',
                              !props.multiple &&
                                props.value === option.value &&
                                'opacity-100',
                              props.multiple &&
                                props.value?.includes(option.value) &&
                                'opacity-100',
                            )}
                          />
                          <div className='min-w-0 flex flex-1 flex-col space-y-1 overflow-hidden'>
                            <span>{option.label}</span>
                            {option.description && (
                              <span className='truncate text-xs text-gray-500'>
                                {option.description}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </div>
                ))}
              </div>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)
